'use client';

import { Icon, Image } from '@petsmart-ui/sparky';
import cn from 'classnames';
import Link from 'next/link';
import type { MouseEvent } from 'react';

import { trackMainNavLinkClick } from '@libs/analytics';
import { usePageLayout } from '@libs/common-next';
import { handleServiceClick } from '@libs/experience-api';

import { getCategoryPath, trackCategoryPath } from '../MainMenu/utils';
import type { DrawerMenuItemProps } from './types';

export function DrawerMenuItem({
    category,
    categoryStack,
    handleCategoryClick,
    closeMenu,
}: Readonly<DrawerMenuItemProps>) {
    const { isAuthenticated, isSoftAuthenticated } = usePageLayout();

    const depth = categoryStack.length - 1;
    const classes = cn(
        'category',
        `category--${category.type ?? 'text'}`,
        category.highlighted && 'highlighted',
        depth > 0 && ['category--thin']
    );

    const categoryPath = categoryStack.slice(1).map(c => c.displayText || '');
    const newPath = getCategoryPath(categoryPath, category.displayText);
    const trackNewPath = trackCategoryPath(newPath);

    function handleLinkClick(event: MouseEvent) {
        handleServiceClick({
            authType: isAuthenticated,
            event,
            link: category.link,
            isSoftAuthenticated,
        });
        trackMainNavLinkClick(trackNewPath);
        closeMenu();
    }

    return (
        <li className={classes} data-testid="header-drawer-menu-item" tabIndex={-1}>
            {category.categories?.length ? (
                <button onClick={() => handleCategoryClick(category)} className="category__button" type="button">
                    <span>{category.displayText}</span>
                    <Icon name="chevron-right" />
                </button>
            ) : (
                <Link
                    className={category.shopAllCategory ? 'category__link category__link--shop-all' : 'category__link'}
                    onClick={event => handleLinkClick(event)}
                    href={category.link ?? '#'}
                    prefetch={false} // https://petsmart.atlassian.net/browse/SB-325
                >
                    {category.type === 'image' ? (
                        <>
                            <Image src={category.imageUrl ?? ''} alt={category.displayText ?? ''} loading="lazy" />
                            <span className="sparky-u-is-vishidden">{category.displayText}</span>
                        </>
                    ) : (
                        <span>{category.displayText}</span>
                    )}
                </Link>
            )}
        </li>
    );
}
